/**
 * Lokaler Entwurf des Handy-Assistenten (MobileInspectionWizard).
 *
 * Android beendet die App gern, während die Systemkamera offen ist
 * (Prozess-Aus), und eine neu geladene Seite fängt von vorn an. Text, Ort,
 * Firma und die schon aufgenommenen Fotos lebten bis dahin nur im React-
 * Zustand des Assistenten. Jetzt legt er seinen Stand hier ab und bietet ihn
 * beim nächsten Öffnen zur Wiederaufnahme an. Die Regeln — was hineinkommt,
 * wann ein Entwurf gilt, wo das Fortsetzen landet — stehen in
 * components/mobile/wizardLogik.ts.
 *
 * Eigene kleine IndexedDB (Muster: offlineQueue.ts), genau EIN Entwurf je
 * Schlüssel (Projekt, ggf. Abschnitt): jedes Speichern ersetzt den vorigen.
 * Fotos sind Base64-Text wie in der Maske und gehen mit.
 *
 * FAIL-OPEN: ohne IndexedDB (privater Modus, gesperrte Website-Daten, Quote
 * voll, hängende Datenbank) passiert nichts — kein Fehler nach außen, kein
 * Warten über die Zeitgrenze hinaus. Der Assistent arbeitet dann wie vorher,
 * nur ohne Sicherung.
 *
 * REIHENFOLGE: alle Zugriffe stellen sich in EINE Kette, in der Reihenfolge
 * des Aufrufs. Ein spätes Speichern kann so kein Löschen überholen — sonst
 * tauchte ein schon übergebener Eintrag als Entwurf wieder auf.
 *
 * Der Speicher prüft den Inhalt nicht: `entwurfLaden` liefert, was liegt;
 * Form und Alter prüft der Leser (`entwurfPruefen` in wizardLogik.ts).
 */

const DB_NAME = 'osia_assistent_entwurf_v1';
const STORE = 'entwuerfe';

/**
 * So lange darf ein Zugriff dauern. Danach gilt er als gescheitert (Rückfall),
 * und die Kette läuft weiter — eine hängende Datenbank darf weder den
 * Assistenten noch die nächsten Zugriffe festhalten.
 */
export const ENTWURF_SPEICHER_ZEITGRENZE_MS = 3000;

/**
 * Schlüssel des Entwurfs: das Projekt, dazu der Abschnitt, falls der Bericht
 * einen hat. Ohne Projekt — und für ein nie gespeichertes ('temp-id', dieselbe
 * Vorbelegung für jedes neue Projekt) — keiner: dann wird nichts gesichert.
 */
export function assistentEntwurfSchluessel(projectId: string | null | undefined, sectionId?: string | null): string | null {
    const projekt = (projectId ?? '').trim();
    if (!projekt || projekt === 'temp-id') return null;
    const abschnitt = (sectionId ?? '').trim();
    // Als JSON-Liste: kein Trennzeichen, das in einer ID vorkommen und zwei Schlüssel verwechseln könnte.
    return JSON.stringify(abschnitt ? [projekt, abschnitt] : [projekt]);
}

/** Liefert die IndexedDB — oder nichts. Darf werfen (gesperrte Website-Daten); das fängt der Speicher ab. */
export type IndexedDbQuelle = () => IDBFactory | null | undefined;

/**
 * Rückfall von `laden`, wenn sich NICHT sagen lässt, ob ein Entwurf liegt
 * (keine IndexedDB, Fehler, Zeitgrenze). Bewusst nicht `null`: „nichts
 * gespeichert“ erlaubte dem Assistenten, den liegenden Entwurf zu löschen oder
 * mit der nächsten Eingabe zu überschreiben — genau den, den er schützen soll.
 */
export const ENTWURF_UNLESBAR: unique symbol = Symbol('entwurf-unlesbar');

export interface EntwurfSpeicher {
    /** true, wenn der Entwurf fest liegt; false bei jedem Fehler (nie ein Wurf). */
    speichern(schluessel: string, entwurf: unknown): Promise<boolean>;
    /** Der gespeicherte Wert, UNGEPRÜFT; null, wenn keiner liegt; ENTWURF_UNLESBAR, wenn unbekannt. */
    laden(schluessel: string): Promise<unknown>;
    /** true, wenn gelöscht (auch, wenn nichts lag); false bei jedem Fehler. */
    loeschen(schluessel: string): Promise<boolean>;
}

const ZEIT_UM = Symbol('entwurf-zeit-um');

/** Wie `mitZeitgrenze` (wizardLogik.ts): die Uhr wird immer abgeräumt, eine späte Antwort bleibt behandelt. */
function mitZeitgrenze<T>(arbeit: Promise<T>, ms: number): Promise<T | typeof ZEIT_UM> {
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const wecker = new Promise<typeof ZEIT_UM>((resolve) => {
        uhr = setTimeout(() => resolve(ZEIT_UM), ms);
    });
    return Promise.race([arbeit, wecker]).finally(() => {
        if (uhr !== undefined) clearTimeout(uhr);
    });
}

/**
 * Fabrik des Speichers — die IndexedDB wird hereingereicht, damit er sich ohne
 * Browser prüfen lässt (das Projekt hat weder jsdom noch fake-indexeddb).
 */
export function erzeugeEntwurfSpeicher(
    quelle: IndexedDbQuelle,
    zeitgrenzeMs: number = ENTWURF_SPEICHER_ZEITGRENZE_MS,
): EntwurfSpeicher {
    let verbindung: Promise<IDBDatabase> | null = null;
    let kette: Promise<unknown> = Promise.resolve();
    /**
     * Noch nicht begonnene Sicherung je Schlüssel. Kommt eine neue, bevor sie
     * läuft, ersetzt sie nur deren Stand — so wartet je Schlüssel höchstens EINE
     * Kopie (samt Base64-Fotos), auch wenn die Datenbank hängt und jede Stufe
     * die volle Zeitgrenze braucht. Ein Löschen beendet das Zusammenfassen:
     * was danach kommt, stellt sich hinter das Löschen.
     */
    const wartendeSicherung = new Map<string, { stand: unknown; ergebnis: Promise<boolean> }>();

    const oeffnen = (): Promise<IDBDatabase> => {
        if (verbindung) return verbindung;
        const versuch = new Promise<IDBDatabase>((resolve, reject) => {
            // Eigene Zeitgrenze fürs Öffnen: hängt es, bliebe der Versuch sonst
            // als `verbindung` stehen, und jeder spätere Zugriff hängte sich samt
            // Kopie (Base64-Fotos) an ein Versprechen, das nie auflöst.
            let abgelaufen = false;
            const uhr = setTimeout(() => {
                abgelaufen = true;
                reject(new Error('IndexedDB antwortet nicht'));
            }, zeitgrenzeMs);
            const scheitern = (fehler: unknown) => {
                clearTimeout(uhr);
                reject(fehler);
            };
            let anfrage: IDBOpenDBRequest;
            try {
                // Wirft `quelle` oder `open` (SecurityError, InvalidStateError): Rückfall.
                const fabrik = quelle();
                if (!fabrik) {
                    scheitern(new Error('IndexedDB nicht verfügbar'));
                    return;
                }
                anfrage = fabrik.open(DB_NAME, 1);
            } catch (fehler) {
                scheitern(fehler);
                return;
            }
            anfrage.onupgradeneeded = () => {
                const db = anfrage.result;
                if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
            };
            anfrage.onsuccess = () => {
                const db = anfrage.result;
                // Zu spät — der Versuch ist schon aufgegeben: Verbindung gleich wieder zu.
                if (abgelaufen) {
                    db.close();
                    return;
                }
                clearTimeout(uhr);
                const vergessen = () => { if (verbindung === versuch) verbindung = null; };
                db.onclose = vergessen;
                // Ein späteres Upgrade (anderer Tab, neue Fassung) nicht blockieren.
                db.onversionchange = () => { db.close(); vergessen(); };
                resolve(db);
            };
            anfrage.onerror = () => scheitern(anfrage.error ?? new Error('IndexedDB lässt sich nicht öffnen'));
            anfrage.onblocked = () => { /* nur Upgrades blockieren; Fassung 1 ändert sich nicht */ };
        });
        verbindung = versuch;
        // Gescheitert: beim nächsten Zugriff neu versuchen (z. B. nach kurz gesperrtem Speicher).
        versuch.catch(() => { if (verbindung === versuch) verbindung = null; });
        return versuch;
    };

    /** Eine Transaktion; löst erst mit ihrem Abschluss auf (`oncomplete`), lehnt bei Fehler oder Abbruch ab. */
    const transaktion = <T>(
        modus: IDBTransactionMode,
        arbeit: (store: IDBObjectStore) => IDBRequest | void,
        ergebnis: (anfrage: IDBRequest | void) => T,
    ): Promise<T> => oeffnen().then(db => new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, modus);
        const anfrage = arbeit(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(ergebnis(anfrage));
        tx.onerror = () => reject(tx.error ?? new Error('Entwurf: Transaktion gescheitert'));
        tx.onabort = () => reject(tx.error ?? new Error('Entwurf: Transaktion abgebrochen'));
    }));

    /**
     * Hinten anstellen. Jeder Zugriff beginnt erst, wenn der vorige fertig,
     * gescheitert oder über die Zeitgrenze ist; jeder löst auf — nie ein Wurf.
     */
    const anstellen = <T>(arbeit: () => Promise<T>, rueckfall: T): Promise<T> => {
        const lauf = kette
            .then(() => mitZeitgrenze(arbeit(), zeitgrenzeMs))
            .then(wert => (wert === ZEIT_UM ? rueckfall : wert), () => rueckfall);
        kette = lauf;
        return lauf;
    };

    return {
        speichern: (schluessel, entwurf) => {
            if (!schluessel) return Promise.resolve(false);
            // Den Stand im Augenblick des Aufrufs festhalten — geschrieben wird erst nach
            // Kette und Öffnen. Nicht kopierbar (DataCloneError): nicht gesichert, kein Wurf.
            let stand: unknown;
            try {
                stand = typeof structuredClone === 'function' ? structuredClone(entwurf) : entwurf;
            } catch {
                return Promise.resolve(false);
            }
            const wartet = wartendeSicherung.get(schluessel);
            if (wartet) {
                wartet.stand = stand;
                return wartet.ergebnis;
            }
            const eintrag: { stand: unknown; ergebnis: Promise<boolean> } = { stand, ergebnis: Promise.resolve(false) };
            eintrag.ergebnis = anstellen(() => {
                // Ab hier läuft sie: spätere Sicherungen stellen sich neu an.
                if (wartendeSicherung.get(schluessel) === eintrag) wartendeSicherung.delete(schluessel);
                return transaktion('readwrite', store => { store.put(eintrag.stand, schluessel); }, () => true);
            }, false);
            wartendeSicherung.set(schluessel, eintrag);
            return eintrag.ergebnis;
        },
        laden: (schluessel) => (!schluessel ? Promise.resolve(null) : anstellen<unknown>(
            () => transaktion('readonly', store => store.get(schluessel), anfrage => (anfrage ? (anfrage.result as unknown) ?? null : null)),
            ENTWURF_UNLESBAR,
        )),
        loeschen: (schluessel) => {
            if (!schluessel) return Promise.resolve(false);
            // Eine noch wartende Sicherung bleibt VOR dem Löschen; spätere fassen nicht mehr in sie hinein.
            wartendeSicherung.delete(schluessel);
            return anstellen(
                () => transaktion('readwrite', store => { store.delete(schluessel); }, () => true),
                false,
            );
        },
    };
}

/** Die IndexedDB des Browsers; schon der Zugriff kann werfen (gesperrte Website-Daten). */
const browserIndexedDb: IndexedDbQuelle = () => (typeof indexedDB === 'undefined' ? null : indexedDB);

const speicher = erzeugeEntwurfSpeicher(browserIndexedDb);

/** Entwurf ablegen — ersetzt den vorigen desselben Schlüssels. false: nicht gesichert (kein Fehler nach außen). */
export const entwurfSpeichern = (schluessel: string, entwurf: unknown): Promise<boolean> => speicher.speichern(schluessel, entwurf);

/** Gespeicherter Entwurf, UNGEPRÜFT (Form und Alter: `entwurfPruefen`); null, wenn keiner da ist; ENTWURF_UNLESBAR, wenn unbekannt. */
export const entwurfLaden = (schluessel: string): Promise<unknown> => speicher.laden(schluessel);

/** Entwurf löschen. false: nicht gelöscht (kein Fehler nach außen). */
export const entwurfLoeschen = (schluessel: string): Promise<boolean> => speicher.loeschen(schluessel);
